import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  MapPin,
  Clock,
  ArrowRight,
  Upload,
  User,
  Mail,
  Link as LinkIcon,
  FileText,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  Terminal,
} from 'lucide-react';
import { Language } from '@/types';
import PageTransition from '@/components/global/PageTransition';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import SEO from '@/components/global/SEO';

interface CareersPageProps {
  lang: Language;
}

interface JobRole {
  id: string;
  isNovaContext?: boolean;
  code: string;
  title: { en: string; ar: string };
  dept: { en: string; ar: string };
  loc: { en: string; ar: string };
  type: { en: string; ar: string };
  desc: { en: string; ar: string };
  reqs: { en: string[]; ar: string[] };
}

const JOBS: JobRole[] = [
  {
    id: 'geoai-architect',
    isNovaContext: true,
    code: 'ENG-LABS // LSM-01',
    title: { en: 'Principal GeoAI Research Scientist', ar: 'كبير باحثي الذكاء الاصطناعي الجغرافي' },
    dept: { en: 'Nova Core Intelligence & Labs', ar: 'مختبرات ذكاء نوفا الأساسي' },
    loc: { en: 'Cairo Sovereign Enclave (Hybrid)', ar: 'مجمع القاهرة السيادي (هجين)' },
    type: { en: 'Full-time', ar: 'دوام كامل' },
    desc: {
      en: 'Lead the pre-training of Large Spatial Models (LSMs) powering VEXL Nova. Engineer vision-language architectures capable of deterministic spatial reasoning, sub-pixel tensor reduction, and zero-hallucination geoprocessing.',
      ar: 'قيادة تدريب النماذج المكانية الكبرى (LSMs) التي تشغل محرك VEXL Nova. هندسة نماذج الرؤية واللغة القادرة على الاستدلال المكاني الحتمي واختزال الموترات بدقة تحت البكسل دون أي هلوسة.'
    },
    reqs: {
      en: [
        'PhD or equivalent research pedigree in Geoinformatics, Computer Vision, or Spatial Machine Learning.',
        'Proven expertise in PyTorch, TensorRT, RasterIO, and training multi-modal foundation models on satellite constellations.',
        'Strong publication track record in venues such as NeurIPS, CVPR, or IEEE IGARSS.'
      ],
      ar: [
        'دكتوراه أو خبرة بحثية مكافئة في المعلوماتية الجغرافية، أو الرؤية الحاسوبية، أو تعلم الآلة المكاني.',
        'خبرة عميقة ومثبتة في PyTorch و TensorRT و RasterIO وتدريب النماذج التأسيسية متعددة الوسائط على صور الأقمار الصناعية.',
        'سجل نشر بحثي متميز في مؤتمرات مثل NeurIPS أو CVPR أو IEEE IGARSS.'
      ]
    }
  },
  {
    id: 'spatial-systems-dev',
    isNovaContext: false,
    code: 'SYS-CORE // GEO-02',
    title: { en: 'Lead Spatial Systems Engineer', ar: 'مهندس أول للأنظمة المكانية' },
    dept: { en: 'Systems & Infrastructure', ar: 'النظم والبنية التحتية' },
    loc: { en: 'Cairo Sovereign Enclave (Hybrid)', ar: 'مجمع القاهرة السيادي (هجين)' },
    type: { en: 'Full-time', ar: 'دوام كامل' },
    desc: {
      en: 'Design high-throughput, low-latency spatial indexing systems. Optimize distributed databases (SpatiaLite, PostGIS, GeoParquet) and build Rust-based geoprocessing calculation engines with strict geodetic guarantees.',
      ar: 'تصميم أنظمة الفهرسة المكانية عالية الأداء ومنخفضة زمن الاستجابة. تحسين قواعد البيانات المكانية الموزعة (SpatiaLite, PostGIS, GeoParquet) وبناء محركات حسابية بلغة Rust خاضعة لضمانات جيوديسية صارمة.'
    },
    reqs: {
      en: [
        '5+ years experience in systems programming (Rust, C++, or Go) and spatial algorithmic optimization.',
        'Mastery of spatial partitioning structures (R-trees, Quadtrees, H3 discrete global grid systems).',
        'Demonstrated track record of optimizing spatial operations on cloud-native and air-gapped infrastructure.'
      ],
      ar: [
        'أكثر من 5 سنوات خبرة في برمجة النظم (Rust أو C++ أو Go) والتحسين الخوارزمي المكاني.',
        'إتقان هياكل التقسيم المكاني (R-trees, Quadtrees, وأنظمة شبكات H3 السداسية العالمية).',
        'سجل حافل في تحسين العمليات المكانية على البنى السحابية والبيئات المعزولة شبكياً (Air-gapped).'
      ]
    }
  },
  {
    id: 'graphics-ui-engineer',
    isNovaContext: false,
    code: 'UI-ENGINE // V2-03',
    title: { en: 'Lead Geospatial UI/UX Architect & Engineer', ar: 'مهندس ومصمم معماري أول لواجهات النظم المكانية' },
    dept: { en: 'Product Engineering', ar: 'هندسة المنتجات والواجهات' },
    loc: { en: 'Remote (Egypt / GMT+2)', ar: 'عن بعد (مصر / GMT+2)' },
    type: { en: 'Full-time', ar: 'دوام كامل' },
    desc: {
      en: 'Transform complex multi-spectral analytics and topological layers into fluid, high-fidelity sovereign interfaces. Enforce Design System V2 across vector tile renderers, WebGL canvas modules, and geodetic data visualizers.',
      ar: 'تحويل التحليلات متعددة الأطياف وطبقات المتجهات المعقدة إلى واجهات سيادية سلسة وعالية الدقة. تطبيق منظومة التصميم V2 عبر مستعرضات بلاط المتجهات ومحركات WebGL ورسوم البيانات الجيوديسية.'
    },
    reqs: {
      en: [
        'Deep mastery of React, TypeScript, TailwindCSS, Framer Motion, and design systems.',
        'Production experience with WebGL, Three.js, Mapbox GL / MapLibre, Deck.gl, and GeoJSON/MVT vector streaming.',
        'Obsession with micro-interaction craft, tabular data typography, and sovereign dark-mode aesthetics.'
      ],
      ar: [
        'إتقان عميق لـ React و TypeScript و TailwindCSS و Framer Motion وبناء أنظمة التصميم.',
        'خبرة عملية إنتاجية في WebGL و Three.js و MapLibre و Deck.gl وبث بيانات المتجهات بصيغة GeoJSON/MVT.',
        'شغف بالتفاصيل الدقيقة والتفاعلات المصغرة والطباعة الرقمية والجماليات السيادية للواجهات المظلمة.'
      ]
    }
  }
];

export const CareersPage: React.FC<CareersPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const [selectedJob, setSelectedJob] = useState<JobRole | null>(null);
  
  // Application Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [note, setNote] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const t = {
    seoTitle: isAr ? 'الوظائف والانضمام للفريق | VEXL' : 'Careers at VEXL — Join the GeoAI Architecture Team',
    seoDesc: isAr
      ? 'انضم إلى فريق فكسل لبناء الجيل القادم من البنية التحتية للذكاء الاصطناعي الجيومكاني والاستدلال المكاني السيادي.'
      : 'Shape the future of spatial intelligence. Engineer foundational Large Spatial Models, distributed GIS kernels, and sovereign interfaces at VEXL.',
    heroBadge: isAr ? 'فرص العمل في فكسل' : 'CAREERS AT VEXL // SOVEREIGN GEOAI',
    heroTitle: isAr ? 'لنصنع مستقبل الذكاء المكاني معاً.' : 'Shape the future of spatial intelligence.',
    heroSubtitle: isAr 
      ? 'نحن نجمع ألمع العقول في مجالات الذكاء الاصطناعي، وعلوم الجيوديسيا، ونظم المعلومات الجغرافية، وهندسة النظم لبناء البنية التحتية للجيل القادم من الاستدلال المكاني الحتمي.'
      : 'We bring together the sharpest minds in artificial intelligence, geodesy, GIS, and systems engineering to build next-generation deterministic spatial reasoning infrastructure.',
    openRolesHeader: isAr ? 'الشواغر الهندسية والبحثية' : 'OPEN POSITIONS',
    openRolesSubtitle: isAr ? 'فرص مفتوحة للباحثين ومهندسي النظم المتميزين.' : 'High-impact roles across our research labs and core engineering teams.',
    requirements: isAr ? 'المتطلبات الأساسية:' : 'Key Requirements:',
    applyBtn: isAr ? 'التقديم على الوظيفة' : 'Apply for Role',
    formTitle: isAr ? 'طلب الانضمام للفريق' : 'Submit Engineering Dossier',
    formName: isAr ? 'الاسم الكامل' : 'Full Name',
    formEmail: isAr ? 'البريد الإلكتروني' : 'Email Address',
    formLinkedin: isAr ? 'رابط ملف LinkedIn / GitHub' : 'LinkedIn / GitHub Profile URL',
    formResume: isAr ? 'السيرة الذاتية (PDF)' : 'Resume / CV (PDF)',
    formNote: isAr ? 'رسالة تعريفية أو ملخص الإنجازات' : 'Cover Letter / Research Accomplishments',
    formSubmit: isAr ? 'إرسال ملف الترشح' : 'Submit Dossier',
    formSubmitting: isAr ? 'جاري الإرسال...' : 'Submitting dossier...',
    formCancel: isAr ? 'إلغاء' : 'Cancel',
    successTitle: isAr ? 'تم استلام طلبك بنجاح!' : 'Dossier Received!',
    successDesc: isAr
      ? 'شكراً لاهتمامك بالانضمام إلى فريق فكسل. سيقوم فريق تقييم الهندسة بمراجعة ملفك والتواصل معك قريباً.'
      : 'Thank you for your application. Our engineering recruiting team will review your dossier and reach out shortly.',
    uploadPlaceholder: isAr ? 'اسحب ملف السيرة الذاتية هنا أو انقر للاختيار' : 'Drag & drop CV (PDF) here, or click to browse',
    closeBtn: isAr ? 'إغلاق' : 'Close',
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResumeName(e.target.files[0].name);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setName('');
      setEmail('');
      setLinkedin('');
      setNote('');
      setResumeName('');
    }, 1200);
  };

  return (
    <PageTransition>
      <SEO
        title={t.seoTitle}
        description={t.seoDesc}
        lang={lang}
      />
      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-24 md:pt-28 pb-24 px-6 ${isAr ? 'font-arabic' : 'font-sans'} relative overflow-hidden`}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        {/* ─── HERO SECTION (Editorial Split) ─── */}
        <section className="max-w-[1400px] mx-auto mb-20 border-b border-[#262C31] pb-16 relative z-10">
          <div
            className={`
              w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start
              ${isAr ? 'text-right' : 'text-left'}
            `}
          >
            {/* Left Column (Headline) */}
            <div className="lg:col-span-7 space-y-3">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold mb-3">
                {t.heroBadge}
              </div>
              <h1
                className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.heroTitle}
              </h1>
            </div>

            {/* Right Column (Sub-Description) */}
            <div className="lg:col-span-5 space-y-4 lg:pt-6">
              <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                {t.heroSubtitle}
              </p>
            </div>
          </div>
        </section>

        {/* ─── ROLES LIST ─── */}
        <section className="max-w-[1400px] mx-auto mb-20 relative z-10">
          <div className="space-y-2 mb-10">
            <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
              {t.openRolesHeader}
            </span>
            <h2
              className="text-2xl md:text-3xl font-medium tracking-tight text-[#EDF0F1]"
              style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
            >
              {t.openRolesSubtitle}
            </h2>
          </div>

          <div className="space-y-6">
            {JOBS.map((job) => (
              <div 
                key={job.id}
                className="group w-full bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 rounded-xl p-6 md:p-8 transition-all duration-200 shadow-sm relative overflow-hidden"
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                  <div className="space-y-4 flex-grow">
                    {/* Metadata line */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-mono text-[#9AA5AC] tracking-wider">
                      {/* Strictly contained Nova branding only for Nova Core AI context */}
                      {job.isNovaContext ? (
                        <span className="px-2.5 py-0.5 rounded-full bg-[#7F77DD]/10 border border-[#7F77DD]/30 text-[#B0A8F8] font-mono text-[10px] font-semibold">
                          {job.dept[lang]}
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full bg-[#05070A] border border-[#262C31] text-[#EDF0F1] font-mono text-[10px] font-semibold">
                          {job.dept[lang]}
                        </span>
                      )}
                      
                      <span className="text-[10px] font-mono text-[#66707A] tnum">{job.code}</span>
                      
                      <span className="flex items-center gap-1 text-[11px]">
                        <MapPin size={12} className="text-[#0E93A3]" />
                        <span>{job.loc[lang]}</span>
                      </span>
                      <span className="flex items-center gap-1 text-[11px]">
                        <Clock size={12} className="text-[#0E93A3]" />
                        <span>{job.type[lang]}</span>
                      </span>
                    </div>

                    {/* Job Title */}
                    <h3 className="text-xl md:text-2xl font-semibold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors">
                      {job.title[lang]}
                    </h3>

                    {/* Description */}
                    <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal max-w-4xl">
                      {job.desc[lang]}
                    </p>

                    {/* Requirements snippet */}
                    <div className="space-y-2 pt-2">
                      <h4 className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#EDF0F1]">
                        {t.requirements}
                      </h4>
                      <ul className={`list-disc ${isAr ? 'pr-4' : 'pl-4'} text-[#9AA5AC] text-xs md:text-sm space-y-1.5 leading-relaxed max-w-4xl`}>
                        {job.reqs[lang].map((req, rIdx) => (
                          <li key={rIdx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex-shrink-0 lg:self-center pt-2 lg:pt-0">
                    <button
                      onClick={() => setSelectedJob(job)}
                      className="w-full lg:w-auto px-5 py-2.5 bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs tracking-wide uppercase rounded-full transition-all flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(14,147,163,0.25)] cursor-pointer"
                    >
                      <span>{t.applyBtn}</span>
                      <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── MODAL FORM OVERLAY ─── */}
        <AnimatePresence>
          {selectedJob && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#05070A]/85 backdrop-blur-md pointer-events-auto"
            >
              <motion.div
                initial={{ scale: 0.96, y: 16 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 16 }}
                transition={{ type: 'spring', duration: 0.4 }}
                className="w-full max-w-xl bg-[#12161A] border border-[#262C31] rounded-2xl shadow-2xl overflow-hidden relative max-h-[90vh] flex flex-col"
              >
                {/* Header */}
                <div className="p-6 border-b border-[#262C31] bg-[#12161A] sticky top-0 z-10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono tracking-wider text-[#0E93A3] uppercase">
                      {selectedJob.dept[lang]} // {selectedJob.type[lang]}
                    </span>
                    <span className="text-[10px] font-mono text-[#66707A] tnum">{selectedJob.code}</span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-[#EDF0F1] leading-tight">
                    {selectedJob.title[lang]}
                  </h2>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto flex-grow space-y-6">
                  {!isSuccess ? (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Name */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#9AA5AC] flex items-center gap-1.5">
                          <User size={12} className="text-[#0E93A3]" />
                          {t.formName}
                        </label>
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full bg-[#05070A] border border-[#262C31] rounded-lg py-2.5 px-3.5 text-[#EDF0F1] text-sm focus:outline-none focus:border-[#0E93A3] transition-colors"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#9AA5AC] flex items-center gap-1.5">
                          <Mail size={12} className="text-[#0E93A3]" />
                          {t.formEmail}
                        </label>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-[#05070A] border border-[#262C31] rounded-lg py-2.5 px-3.5 text-[#EDF0F1] text-sm focus:outline-none focus:border-[#0E93A3] transition-colors"
                        />
                      </div>

                      {/* LinkedIn */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#9AA5AC] flex items-center gap-1.5">
                          <LinkIcon size={12} className="text-[#0E93A3]" />
                          {t.formLinkedin}
                        </label>
                        <input
                          type="url"
                          required
                          value={linkedin}
                          onChange={(e) => setLinkedin(e.target.value)}
                          className="w-full bg-[#05070A] border border-[#262C31] rounded-lg py-2.5 px-3.5 text-[#EDF0F1] text-sm focus:outline-none focus:border-[#0E93A3] transition-colors"
                        />
                      </div>

                      {/* Resume Upload */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#9AA5AC] flex items-center gap-1.5">
                          <Upload size={12} className="text-[#0E93A3]" />
                          {t.formResume}
                        </label>
                        <div className="relative border border-dashed border-[#262C31] hover:border-[#0E93A3]/60 rounded-lg bg-[#05070A] p-6 flex flex-col items-center justify-center cursor-pointer transition-colors group">
                          <input
                            type="file"
                            accept=".pdf"
                            required={!resumeName}
                            onChange={handleFileChange}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                          />
                          <Upload className="w-6 h-6 text-[#66707A] group-hover:text-[#0E93A3] transition-colors mb-2" strokeWidth={1.5} />
                          <span className="text-xs text-[#9AA5AC] font-medium text-center leading-relaxed">
                            {resumeName ? (
                              <span className="text-[#0E93A3] font-bold font-mono">{resumeName}</span>
                            ) : (
                              t.uploadPlaceholder
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Cover letter notes */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#9AA5AC] flex items-center gap-1.5">
                          <FileText size={12} className="text-[#0E93A3]" />
                          {t.formNote}
                        </label>
                        <textarea
                          rows={3}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          className="w-full bg-[#05070A] border border-[#262C31] rounded-lg py-2.5 px-3.5 text-[#EDF0F1] text-sm focus:outline-none focus:border-[#0E93A3] transition-colors resize-none"
                        />
                      </div>

                      {/* Footer Actions */}
                      <div className="flex gap-3 pt-3 border-t border-[#262C31]">
                        <button
                          type="button"
                          onClick={() => setSelectedJob(null)}
                          className="flex-1 py-2.5 border border-[#262C31] hover:border-[#9AA5AC] text-[#9AA5AC] hover:text-[#EDF0F1] font-semibold text-xs tracking-wider uppercase rounded-full transition-colors cursor-pointer"
                        >
                          {t.formCancel}
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 py-2.5 bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs tracking-wider uppercase rounded-full transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer shadow-[0_0_15px_rgba(14,147,163,0.3)]"
                        >
                          {isSubmitting ? (
                            <span>{t.formSubmitting}</span>
                          ) : (
                            <>
                              <span>{t.formSubmit}</span>
                              <ChevronRight size={13} className={isAr ? 'rotate-180' : ''} />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col items-center py-10 text-center space-y-4"
                    >
                      <div className="w-14 h-14 bg-[#0E93A3]/10 rounded-full flex items-center justify-center border border-[#0E93A3]/30">
                        <CheckCircle2 size={28} className="text-[#0E93A3]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#EDF0F1]">{t.successTitle}</h3>
                      <p className="text-[#9AA5AC] text-xs max-w-sm leading-relaxed">
                        {t.successDesc}
                      </p>
                      <button
                        onClick={() => {
                          setSelectedJob(null);
                          setIsSuccess(false);
                        }}
                        className="px-6 py-2.5 bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs tracking-wider uppercase rounded-full transition-colors cursor-pointer"
                      >
                        {t.closeBtn}
                      </button>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PageTransition>
  );
};

export default CareersPage;
